import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import {
  MapPin,
  AlertTriangle,
  Play,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Plus,
  Trash2,
  ArrowRightLeft,
  Loader2,
  UserPlus,
  CheckCircle,
  Calendar,
} from 'lucide-react';
import {
  format,
  addDays,
  startOfWeek,
  isToday,
  parseISO,
  isValid,
} from 'date-fns';
import {
  scheduleService,
  type WeeklyPlan,
  type WeeklyPlanSite,
  type WeeklyPlanDay,
  type OpenSite,
} from '../../services/scheduleService';
import { claimableSites } from './claimAndAdd';
import { siteSubtitle } from './siteLabel';
import { api } from '../../services/api';
import { useToast } from '@/hooks/useToast';
import { DayPicker } from './components/DayPicker';
import { RainEventChip } from './components/RainEventChip';
import { DirectionsLink } from '../../components/DirectionsLink';
import { isRainVisit } from './rainVisit';
import { moveWithin } from './routeOrder';
import { LearnButton } from '../../components/learn/LearnGuide';
import {
  attentionLabel,
  isAttentionSite,
  isDueThisWeek,
} from './attentionSites';
import { SendClock } from './components/SendClock';

// Shape of a recently-completed inspection (merged in from the old Actions tab).
type CompletedInspection = {
  id: string;
  site_id: string;
  site_name: string;
  client_name?: string | null;
  city: string;
  state: string;
  conducted_date: string;
  /** JIM-1003-05: when a held report will be emailed to the client. */
  send_scheduled_at?: string | null;
};

function getMonday(d: Date): string {
  const start = startOfWeek(d, { weekStartsOn: 1 });
  return format(start, 'yyyy-MM-dd');
}

export function LiteSchedulePage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const location = useLocation();

  // Surface the message passed by the inspection flow's Skip/Done buttons
  // (it was silently dropped before — Jim 2026-08-20). One-shot per arrival.
  const arrivalMessageRef = useRef<string | null>(null);
  useEffect(() => {
    const message = (location.state as { message?: string } | null)?.message;
    if (message && arrivalMessageRef.current !== message) {
      arrivalMessageRef.current = message;
      toast({ title: message });
      window.history.replaceState({}, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const [currentWeekStart, setCurrentWeekStart] = useState(
    getMonday(new Date())
  );
  const [expandedDays, setExpandedDays] = useState<Set<string>>(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return new Set([today]);
  });
  // Default the unscheduled ("Due, not scheduled") list OPEN so inspectors plan
  // per-site from the day pickers without reaching for Build Schedule (#262).
  const [showUnscheduled, setShowUnscheduled] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showUnclaimed, setShowUnclaimed] = useState(false);
  const [showClaimable, setShowClaimable] = useState<boolean | null>(null);
  const [claimDayPickerFor, setClaimDayPickerFor] = useState<string | null>(
    null
  );
  const [dayPickerFor, setDayPickerFor] = useState<string | null>(null);
  const [addDayPickerFor, setAddDayPickerFor] = useState<string | null>(null);

  // Fetch weekly plan
  const { data: plan, isLoading } = useQuery({
    queryKey: ['weekly-plan', currentWeekStart],
    queryFn: () => scheduleService.getWeeklyPlan(currentWeekStart),
  });

  // Mutations
  // JIM-1006-04b: Move up / Move down on a day's rows; the day shows the new
  // order at once and the server keeps it as the route order the plan reads.
  const orderMutation = useMutation({
    mutationFn: ({ ids }: { day: string; ids: string[] }) =>
      scheduleService.setDayOrder(ids),
    onMutate: async ({ day, ids }) => {
      await queryClient.cancelQueries({ queryKey: ['weekly-plan'] });
      queryClient.setQueriesData<WeeklyPlan>(
        { queryKey: ['weekly-plan'] },
        (old) =>
          old
            ? {
                ...old,
                days: old.days.map((d) =>
                  d.date === day
                    ? {
                        ...d,
                        sites: ids
                          .map((id) =>
                            d.sites.find((s) => s.inspection_id === id)
                          )
                          .filter((s): s is WeeklyPlanSite => !!s),
                      }
                    : d
                ),
              }
            : old
      );
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-plan'] });
      toast({ title: 'Could not save the order', variant: 'destructive' });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['weekly-plan'] }),
  });
  const moveInDay = (
    day: WeeklyPlanDay,
    inspectionId: string,
    direction: 'up' | 'down'
  ) => {
    const next = moveWithin(day.sites, inspectionId, direction);
    if (next === day.sites) return;
    orderMutation.mutate({
      day: day.date,
      ids: next.map((s) => s.inspection_id),
    });
  };

  // JIM-1014-14 (Jim 10/14: "I want to bake that in their brain"): confirming the
  // week stamps every planned visit so the Hub map shows it as committed.
  const confirmWeekMutation = useMutation({
    mutationFn: () => scheduleService.confirmWeek(currentWeekStart),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['weekly-plan'] });
      toast({
        title:
          r.confirmed === 1
            ? 'Week confirmed — 1 visit'
            : `Week confirmed — ${r.confirmed} visits`,
      });
    },
    onError: () =>
      toast({ title: 'Could not confirm the week', variant: 'destructive' }),
  });

  const rescheduleMutation = useMutation({
    mutationFn: ({
      inspectionId,
      newDate,
      everyWeek,
    }: {
      inspectionId: string;
      newDate: string;
      everyWeek?: boolean;
    }) =>
      scheduleService.rescheduleInspection(inspectionId, newDate, everyWeek),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-plan'] });
      setDayPickerFor(null);
      toast({ title: 'Inspection rescheduled' });
    },
    onError: () =>
      toast({ title: 'Failed to reschedule', variant: 'destructive' }),
  });

  // Weeks the background auto-builder must leave alone this session. Declared
  // before the mutations so removeMutation can mark a week: deleting the last
  // row makes the week empty, and without this the auto-builder immediately
  // re-created the just-removed site ("deleted inspection reappears in
  // seconds", Jim 2026-07-17). The manual "Auto-build the whole week" button
  // was retired 2026-09-05 (Jim: "why is it stacking all these on Monday");
  // only this background fill remains.
  const autoBuiltWeeks = useRef<Set<string>>(new Set());

  const removeMutation = useMutation({
    mutationFn: (inspectionId: string) =>
      scheduleService.removeFromPlan(inspectionId),
    // Optimistically pull the inspection out of the plan so the row vanishes on
    // tap; invalidate-then-refetch alone left it on screen until refresh (#290).
    onMutate: async (inspectionId: string) => {
      // Guard the auto-builder HERE, not onSuccess: the optimistic removal
      // below can empty the plan and fire the auto-build effect before the
      // DELETE resolves, which would race a rebuild against the delete.
      autoBuiltWeeks.current.add(currentWeekStart);
      await queryClient.cancelQueries({ queryKey: ['weekly-plan'] });
      const prev = queryClient.getQueriesData<WeeklyPlan>({
        queryKey: ['weekly-plan'],
      });
      queryClient.setQueriesData<WeeklyPlan>(
        { queryKey: ['weekly-plan'] },
        (old) =>
          old
            ? {
                ...old,
                days: old.days.map((d) => {
                  const sites = d.sites.filter(
                    (s) => s.inspection_id !== inspectionId
                  );
                  return { ...d, sites, total_sites: sites.length };
                }),
              }
            : old
      );
      return { prev };
    },
    onError: (_e, _id, ctx) => {
      ctx?.prev?.forEach(([key, data]) => queryClient.setQueryData(key, data));
      toast({ title: 'Failed to remove', variant: 'destructive' });
    },
    onSuccess: () => toast({ title: 'Removed from plan' }),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ['weekly-plan'] }),
  });

  const buildPlanMutation = useMutation({
    mutationFn: () => scheduleService.buildMyPlan(currentWeekStart),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['weekly-plan'] });
      if (data.created > 0) {
        toast({
          title: `Schedule built: ${data.created} site${data.created !== 1 ? 's' : ''} added`,
        });
      } else if (data.skipped > 0) {
        toast({ title: 'Schedule already built for this week' });
      } else {
        toast({ title: 'No sites due this week' });
      }
    },
    onError: (error: any) =>
      toast({
        title: error?.response?.data?.detail || 'Failed to build schedule',
        variant: 'destructive',
      }),
  });

  const addToPlanMutation = useMutation({
    mutationFn: ({
      siteId,
      scheduledDate,
    }: {
      siteId: string;
      scheduledDate: string;
    }) => scheduleService.addToPlan(siteId, scheduledDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-plan'] });
      setAddDayPickerFor(null);
      toast({ title: 'Added to plan' });
    },
    onError: (error: any) =>
      toast({
        title: error?.response?.data?.detail || 'Failed to add to plan',
        variant: 'destructive',
      }),
  });

  // --- Merged from the old Actions tab: unclaimed-nearby + recently-completed ---
  const { data: unclaimedData, error: unclaimedError } = useQuery({
    queryKey: ['unclaimed-nearby-sites'],
    queryFn: () => scheduleService.getUnclaimedNearbySites(25),
    retry: false,
  });

  // JIM-1020-09 (Jim 10/20): the claimable sites on active projects belong on
  // this tab, so an inspector adds one to the week in one tap instead of
  // hunting through Sites and claiming each first. Same cache as the Sites tab.
  const { data: myAccess } = useQuery({
    queryKey: ['my-access'],
    queryFn: () => scheduleService.getMyAccess(),
  });
  const claimable = claimableSites(myAccess?.group_sites);
  const claimableOpen = showClaimable ?? claimable.length <= 10;

  // Only my own visits: for an admin the unfiltered list was everyone's
  // (Jim 2026-10-20, Robert's Westfork visit on Jim's Schedule tab).
  const userId = useAuthStore((s) => s.user?.id);
  const { data: completedData } = useQuery({
    queryKey: ['my-completed-inspections', userId],
    enabled: !!userId,
    queryFn: async () => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const res = await api.get<{
        items: Array<{
          id: string;
          site_id: string;
          site_name?: string;
          client_name?: string | null;
          site?: { name?: string; city?: string; state?: string };
          city?: string;
          state?: string;
          conducted_date: string;
          send_scheduled_at?: string | null;
        }>;
      }>(
        `/inspections/?status=approved&status=submitted&date_from=${sevenDaysAgo.toISOString().split('T')[0]}&inspector_id=${userId}&size=20&skip_count=true`
      );
      return (res.data.items || []).map((i) => ({
        id: i.id,
        site_id: i.site_id,
        site_name: i.site_name || i.site?.name || 'Unknown',
        client_name: i.client_name ?? null,
        city: i.site?.city || i.city || '',
        state: i.site?.state || i.state || '',
        conducted_date: i.conducted_date,
        send_scheduled_at: i.send_scheduled_at ?? null,
      })) as CompletedInspection[];
    },
  });

  const claimMutation = useMutation({
    mutationFn: (siteId: string) => scheduleService.claimSite(siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unclaimed-nearby-sites'] });
      queryClient.invalidateQueries({ queryKey: ['weekly-plan'] });
      // JIM-0929-12: the "Due, not scheduled" list reads this query — without
      // the refresh a claimed site only appeared after a reload.
      queryClient.invalidateQueries({ queryKey: ['my-open-sites'] });
      // JIM-1030-08: Claim now also sits on the Unclaimed group sites rows,
      // which read my-access.
      queryClient.invalidateQueries({ queryKey: ['my-access'] });
      toast({ title: 'Site claimed' });
    },
    onError: () =>
      toast({ title: 'Failed to claim site', variant: 'destructive' }),
  });

  // JIM-1030-08 (Gordon, Meet 10/30): a group site goes on a day WITHOUT
  // claiming it first — the visit is the inspector's, the site stays the
  // group's. Claim is its own button for when they do want the site.
  const addUnclaimedMutation = useMutation({
    mutationFn: ({
      siteId,
      scheduledDate,
    }: {
      siteId: string;
      scheduledDate: string;
    }) => scheduleService.addToPlan(siteId, scheduledDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-plan'] });
      queryClient.invalidateQueries({ queryKey: ['my-open-sites'] });
      setClaimDayPickerFor(null);
      toast({ title: 'Added to your week' });
    },
    onError: (error: { response?: { data?: { detail?: string } } }) =>
      toast({
        title: error.response?.data?.detail || 'Could not add the site',
        variant: 'destructive',
      }),
  });

  // Inspector's true inspection load — the same source the old Actions tab used
  // (/sites/my-assigned-open). The pulse pills count a site even when it's
  // already on the calendar, so they reflect real overdue load (not just the
  // unscheduled remainder, which is what plan.unscheduled_sites would give).
  const { data: openSitesData } = useQuery({
    queryKey: ['my-open-sites'],
    queryFn: () => scheduleService.getMyOpenSites(),
  });
  const openSites = openSitesData?.items ?? [];

  // Pulse = true load (overdue work counts even if it's already scheduled).
  const overdueCount = openSites.filter((s) => s.is_overdue).length;
  const dueThisWeekCount = openSites.filter(
    (s) => !s.is_overdue && isDueThisWeek(s)
  ).length;

  // "Due, not scheduled" backlog = needs inspection AND not already on the
  // week's calendar, so a row here never duplicates a day-section row.
  const scheduledSiteIds = new Set(
    (plan?.days ?? []).flatMap((d) => d.sites.map((s) => s.site_id))
  );
  const attentionSites = openSites
    .filter((s) => isAttentionSite(s, scheduledSiteIds))
    .sort((a, b) => (b.days_overdue ?? 0) - (a.days_overdue ?? 0));

  // Auto-build in the background when you land on an EMPTY current or next week
  // that has due sites — so moving into the next week reflects last week's days
  // without anyone hitting "Build Schedule" (Chris). buildMyPlan already mirrors
  // last week's weekday per site. Scoped to this/next week and run once per week
  // per session so navigating never spams builds or generates far-future weeks.
  // (autoBuiltWeeks is declared above the mutations — removals mark the week.)
  useEffect(() => {
    if (!plan || buildPlanMutation.isPending) return;
    const thisMonday = getMonday(new Date());
    const next = new Date();
    next.setDate(next.getDate() + 7);
    const nextMonday = getMonday(next);
    if (currentWeekStart !== thisMonday && currentWeekStart !== nextMonday)
      return;
    if (autoBuiltWeeks.current.has(currentWeekStart)) return;
    const scheduledCount = plan.days.reduce((n, d) => n + d.sites.length, 0);
    if (scheduledCount === 0 && attentionSites.length > 0) {
      autoBuiltWeeks.current.add(currentWeekStart);
      buildPlanMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, currentWeekStart, attentionSites.length]);

  const unclaimedSites = unclaimedData?.items ?? [];
  const completedSites = completedData ?? [];

  const navigateWeek = (direction: number) => {
    const current = new Date(currentWeekStart + 'T00:00:00');
    const next = addDays(current, direction * 7);
    setCurrentWeekStart(format(next, 'yyyy-MM-dd'));
  };

  const toggleDay = (dateStr: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr);
      else next.add(dateStr);
      return next;
    });
  };

  const isCurrentWeek = currentWeekStart === getMonday(new Date());

  const formatWeekLabel = () => {
    if (!plan) return 'Loading...';
    const start = new Date(plan.week_start + 'T00:00:00');
    const end = new Date(plan.week_end + 'T00:00:00');
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
  };

  return (
    <div className="space-y-4">
      {/* At-a-glance pulse (merged from the old Actions tab) */}
      {plan && (
        <div className="grid grid-cols-3 gap-2">
          <StatPill
            label="Overdue"
            value={overdueCount}
            tone="danger"
            icon={<AlertTriangle className="w-4 h-4" />}
          />
          <StatPill
            label="Due this week"
            value={dueThisWeekCount}
            tone="warning"
            icon={<Calendar className="w-4 h-4" />}
          />
          <StatPill
            label="Unclaimed"
            value={claimable.length}
            tone="primary"
            icon={<UserPlus className="w-4 h-4" />}
          />
        </div>
      )}

      {/* Week Header */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700 p-4">
        {/* JIM-1005-05: the little book opens the page's click-through. */}
        <div className="flex justify-end -mt-1 -mr-1 mb-1">
          <LearnButton guide="schedule" />
        </div>
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateWeek(-1)}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="text-center">
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">
              {formatWeekLabel()}
            </h1>
            {plan && (
              <div className="flex items-center justify-center gap-3 mt-1 text-xs">
                {plan.summary.total_auto_scheduled > 0 && (
                  <span className="text-green-600 dark:text-green-400">
                    {plan.summary.total_auto_scheduled} auto
                  </span>
                )}
                {plan.summary.total_manually_added > 0 && (
                  <span className="text-blue-600 dark:text-blue-400">
                    {plan.summary.total_manually_added} added
                  </span>
                )}
                {plan.summary.total_delegated > 0 && (
                  <span className="text-purple-600 dark:text-purple-400">
                    {plan.summary.total_delegated} delegated
                  </span>
                )}
                {plan.summary.total_sites === 0 && (
                  <span className="text-slate-400">No inspections planned</span>
                )}
              </div>
            )}
            {plan && plan.summary.total_sites > 0 && (
              <div className="mt-2">
                {plan.summary.total_confirmed === plan.summary.total_sites ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Week confirmed
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => confirmWeekMutation.mutate()}
                    disabled={confirmWeekMutation.isPending}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {plan.summary.total_confirmed > 0
                      ? `Confirm my week (${plan.summary.total_confirmed} of ${plan.summary.total_sites} confirmed)`
                      : 'Confirm my week'}
                  </button>
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => navigateWeek(1)}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
          >
            <ChevronRight className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
        </div>
        {!isCurrentWeek && (
          <button
            onClick={() => setCurrentWeekStart(getMonday(new Date()))}
            className="w-full mt-2 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700"
          >
            Back to this week
          </button>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
        </div>
      )}

      {/* Day Sections (Mon–Sun since JIM-1030-08) */}
      {plan &&
        plan.days.map((day) => {
          return (
            <DaySection
              key={day.date}
              day={day}
              isExpanded={expandedDays.has(day.date)}
              onToggle={() => toggleDay(day.date)}
              dayPickerFor={dayPickerFor}
              setDayPickerFor={setDayPickerFor}
              weekDays={plan.days}
              onReschedule={(inspectionId, newDate, everyWeek) =>
                rescheduleMutation.mutate({ inspectionId, newDate, everyWeek })
              }
              onRemove={(inspectionId) => removeMutation.mutate(inspectionId)}
              onMove={(inspectionId, direction) =>
                moveInDay(day, inspectionId, direction)
              }
            />
          );
        })}

      {/* Claimed, due, not yet scheduled (Jim 11/2 wording) — the inspector's
          own overdue/due sites not yet on the calendar. Danger
          accent + distinct name so it doesn't read as the amber "Sites to
          Schedule" pool above. (True overdue load lives in the pulse pills.) */}
      {attentionSites.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-danger-200 dark:border-danger-800">
          <button
            onClick={() => setShowUnscheduled(!showUnscheduled)}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-danger-500" />
              <span className="font-medium text-slate-900 dark:text-white text-sm">
                Claimed, due, not yet scheduled
              </span>
              <span className="text-xs bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 px-2 py-0.5 rounded-full">
                {attentionSites.length}
              </span>
            </div>
            {showUnscheduled ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </button>
          {showUnscheduled && (
            <div className="border-t border-danger-100 dark:border-danger-800 divide-y divide-slate-100 dark:divide-slate-700">
              {attentionSites.map((site) => (
                <div key={site.id} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                        {site.name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {site.client_name && `${site.client_name} · `}
                        {site.city}
                        {site.city && site.state ? ', ' : ''}
                        {site.state}
                        {(site.city || site.state) && ' · '}
                        <span
                          className={
                            site.is_overdue
                              ? 'text-danger-600 dark:text-danger-400 font-medium'
                              : ''
                          }
                        >
                          {attentionLabel(site)}
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Link
                        to={`/lite/inspect/${site.id}`}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
                      >
                        <Play className="w-3 h-3" />
                        Inspect
                      </Link>
                      <button
                        onClick={() =>
                          setAddDayPickerFor(
                            addDayPickerFor === site.id ? null : site.id
                          )
                        }
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/40"
                      >
                        <Plus className="w-3 h-3" />
                        Add
                      </button>
                    </div>
                  </div>
                  {addDayPickerFor === site.id && (
                    <div className="mt-2">
                      <DayPicker
                        weekDays={plan?.days ?? []}
                        onSelect={(date) =>
                          addToPlanMutation.mutate({
                            siteId: site.id,
                            scheduledDate: date,
                          })
                        }
                        onClose={() => setAddDayPickerFor(null)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* JIM-1020-09: unclaimed sites on active projects — claim and add in one tap */}
      {claimable.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700">
          <button
            onClick={() => setShowClaimable(!claimableOpen)}
            aria-expanded={claimableOpen}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-primary-500" />
              <span className="font-medium text-slate-900 dark:text-white text-sm">
                Unclaimed group sites
              </span>
              <span className="text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full">
                {claimable.length}
              </span>
            </div>
            {claimableOpen ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </button>
          {claimableOpen && (
            <div className="border-t border-slate-100 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
              {claimable.map((site) => (
                <div key={site.id} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <Link
                        to={`/lite/sites/${site.id}`}
                        className="block text-sm font-medium text-slate-900 dark:text-white truncate"
                      >
                        {site.name}
                      </Link>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {siteSubtitle(site)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => claimMutation.mutate(site.id)}
                      disabled={
                        claimMutation.isPending &&
                        claimMutation.variables === site.id
                      }
                      className="px-2.5 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800 rounded-lg flex-shrink-0 disabled:opacity-50"
                      data-testid={`claim-${site.id}`}
                    >
                      Claim
                    </button>
                    <button
                      onClick={() =>
                        setClaimDayPickerFor(
                          claimDayPickerFor === site.id ? null : site.id
                        )
                      }
                      disabled={
                        addUnclaimedMutation.isPending &&
                        addUnclaimedMutation.variables?.siteId === site.id
                      }
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg flex-shrink-0 disabled:opacity-60"
                    >
                      <Plus className="w-3 h-3" />
                      Add to week
                    </button>
                  </div>
                  {claimDayPickerFor === site.id && (
                    <div className="mt-2">
                      <DayPicker
                        weekDays={plan?.days ?? []}
                        onSelect={(date) =>
                          addUnclaimedMutation.mutate({
                            siteId: site.id,
                            scheduledDate: date,
                          })
                        }
                        onClose={() => setClaimDayPickerFor(null)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recently completed (merged from the old Actions tab) */}
      {completedSites.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700">
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-success-500" />
              <span className="font-medium text-slate-900 dark:text-white text-sm">
                Recently completed
              </span>
              <span className="text-xs bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300 px-2 py-0.5 rounded-full">
                {completedSites.length}
              </span>
            </div>
            {showCompleted ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </button>
          {showCompleted && (
            <div className="border-t border-slate-100 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
              {completedSites.map((insp) => (
                <Link
                  key={insp.id}
                  to={`/lite/sites/${insp.site_id}`}
                  className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/30"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                      {insp.site_name}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {insp.client_name && `${insp.client_name} · `}
                      {insp.city}
                      {insp.city && insp.state ? ', ' : ''}
                      {insp.state}
                      {insp.conducted_date &&
                        isValid(parseISO(insp.conducted_date)) && (
                          <>
                            {' · '}
                            {format(parseISO(insp.conducted_date), 'MMM d')}
                          </>
                        )}
                    </p>
                  </div>
                  {insp.send_scheduled_at ? (
                    <SendClock sendsAt={insp.send_scheduled_at} />
                  ) : (
                    <CheckCircle className="w-4 h-4 text-success-500 flex-shrink-0" />
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No home location set — the only discovery path to unclaimed sites */}
      {unclaimedError && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-primary-800 dark:text-primary-200">
                Set your home location
              </p>
              <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                Add a home location in your profile to see unclaimed sites in
                your area.
              </p>
              <Link
                to="/lite/settings"
                className="text-xs text-primary-700 dark:text-primary-300 underline mt-2 inline-block"
              >
                Go to Settings
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Unclaimed sites nearby (merged from the old Actions tab) */}
      {unclaimedSites.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700">
          <button
            onClick={() => setShowUnclaimed(!showUnclaimed)}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-primary-500" />
              <span className="font-medium text-slate-900 dark:text-white text-sm">
                Find sites near you
              </span>
              <span className="text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full">
                {unclaimedSites.length}
              </span>
            </div>
            {showUnclaimed ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </button>
          {showUnclaimed && (
            <div className="border-t border-slate-100 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
              {unclaimedSites.map((site) => (
                <UnclaimedRow
                  key={site.id}
                  site={site}
                  onClaim={() => claimMutation.mutate(site.id)}
                  isClaiming={
                    claimMutation.isPending &&
                    claimMutation.variables === site.id
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Compact at-a-glance stat for the schedule pulse strip.
function StatPill({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: number | string;
  tone: 'danger' | 'warning' | 'primary';
  icon: ReactNode;
}) {
  const tones: Record<typeof tone, string> = {
    danger:
      'text-danger-600 dark:text-danger-400 bg-danger-50 dark:bg-danger-900/20',
    warning:
      'text-warning-600 dark:text-warning-400 bg-warning-50 dark:bg-warning-900/20',
    primary:
      'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20',
  };
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2 flex items-center gap-2">
      <span
        className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${tones[tone]}`}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-lg font-bold leading-none text-slate-900 dark:text-white">
          {value}
        </p>
        <p className="text-2xs text-slate-500 dark:text-slate-400 truncate">
          {label}
        </p>
      </div>
    </div>
  );
}

// Unclaimed-nearby row with a Claim action (merged from the old Actions tab).
function UnclaimedRow({
  site,
  onClaim,
  isClaiming,
}: {
  site: OpenSite;
  onClaim: () => void;
  isClaiming: boolean;
}) {
  return (
    <div className="px-4 py-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
          {site.name}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
          {site.client_name && `${site.client_name} · `}
          {site.city}
          {site.city && site.state ? ', ' : ''}
          {site.state}
          {site.distance_miles != null && (
            <span className="ml-2 text-primary-600 dark:text-primary-400">
              {site.distance_miles} mi
            </span>
          )}
        </p>
      </div>
      <button
        onClick={onClaim}
        disabled={isClaiming}
        className="flex-shrink-0 px-3 py-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg text-xs font-medium flex items-center gap-1.5 hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors disabled:opacity-50"
      >
        {isClaiming ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <UserPlus className="w-3 h-3" />
        )}
        {isClaiming ? 'Claiming' : 'Claim'}
      </button>
    </div>
  );
}

// Day Section Component
function DaySection({
  day,
  isExpanded,
  onToggle,
  dayPickerFor,
  setDayPickerFor,
  weekDays,
  onReschedule,
  onRemove,
  onMove,
}: {
  day: WeeklyPlanDay;
  isExpanded: boolean;
  onToggle: () => void;
  dayPickerFor: string | null;
  setDayPickerFor: (id: string | null) => void;
  weekDays: WeeklyPlanDay[];
  onReschedule: (
    inspectionId: string,
    newDate: string,
    everyWeek?: boolean
  ) => void;
  onRemove: (inspectionId: string) => void;
  onMove: (inspectionId: string, direction: 'up' | 'down') => void;
}) {
  const dayDate = new Date(day.date + 'T00:00:00');
  const today = isToday(dayDate);

  return (
    <div
      className={`bg-white dark:bg-slate-800 rounded-xl shadow-soft border ${
        today
          ? 'border-primary-300 dark:border-primary-700 ring-1 ring-primary-200 dark:ring-primary-800'
          : 'border-slate-200 dark:border-slate-700'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4"
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
              today
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
            }`}
          >
            {format(dayDate, 'd')}
          </div>
          <div className="text-left">
            <p
              className={`font-medium text-sm ${
                today
                  ? 'text-primary-700 dark:text-primary-300'
                  : 'text-slate-900 dark:text-white'
              }`}
            >
              {day.day_of_week}
              {today && (
                <span className="ml-2 text-xs font-normal text-primary-500">
                  Today
                </span>
              )}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {format(dayDate, 'MMM d')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {day.total_sites > 0 && (
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                today
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
              }`}
            >
              {day.total_sites} site{day.total_sites !== 1 ? 's' : ''}
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-slate-100 dark:border-slate-700">
          {day.sites.length === 0 ? (
            <p className="p-4 text-sm text-slate-400 dark:text-slate-500 text-center">
              No inspections scheduled
            </p>
          ) : (
            <div className="divide-y divide-slate-50 dark:divide-slate-700/50">
              {day.sites.map((site, index) => (
                <PlanSiteCard
                  key={site.inspection_id}
                  site={site}
                  position={index}
                  lastPosition={day.sites.length - 1}
                  onMove={
                    site.completed
                      ? undefined
                      : (direction) => onMove(site.inspection_id, direction)
                  }
                  showDayPicker={dayPickerFor === site.inspection_id}
                  onToggleDayPicker={() =>
                    setDayPickerFor(
                      dayPickerFor === site.inspection_id
                        ? null
                        : site.inspection_id
                    )
                  }
                  weekDays={weekDays}
                  currentDate={day.date}
                  onReschedule={onReschedule}
                  onRemove={onRemove}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Plan Site Card Component
function PlanSiteCard({
  site,
  showDayPicker,
  onToggleDayPicker,
  weekDays,
  currentDate,
  onReschedule,
  onRemove,
  assignMode,
  position,
  lastPosition,
  onMove,
}: {
  site: WeeklyPlanSite;
  showDayPicker: boolean;
  onToggleDayPicker: () => void;
  weekDays: WeeklyPlanDay[];
  currentDate: string;
  onReschedule: (
    inspectionId: string,
    newDate: string,
    everyWeek?: boolean
  ) => void;
  onRemove: (inspectionId: string) => void;
  assignMode?: boolean;
  /** Where the row sits in its day, for the Move up / Move down buttons. */
  position?: number;
  lastPosition?: number;
  onMove?: (direction: 'up' | 'down') => void;
}) {
  const sourceColors = {
    auto_scheduled:
      'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    manually_added:
      'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    delegated:
      'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  };
  const sourceLabels = {
    auto_scheduled: 'Auto',
    manually_added: 'Added',
    delegated: 'Delegated',
  };

  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        {onMove && position !== undefined && lastPosition !== undefined && (
          // JIM-1006-04b: the inspector puts the day in driving order.
          <div className="flex flex-col flex-shrink-0 -ml-1">
            <button
              type="button"
              onClick={() => onMove('up')}
              disabled={position === 0}
              aria-label={`Move up: ${site.site_name}`}
              className="p-0.5 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-25"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => onMove('down')}
              disabled={position === lastPosition}
              aria-label={`Move down: ${site.site_name}`}
              className="p-0.5 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-25"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/lite/sites/${site.site_id}`}
              className="text-sm font-medium text-slate-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 truncate"
            >
              {site.site_name}
              {site.completed && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40">
                  Completed
                </span>
              )}
            </Link>
            <span
              className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sourceColors[site.source]}`}
            >
              {sourceLabels[site.source]}
            </span>
            {isRainVisit(site.inspection_type) && <RainEventChip />}
          </div>
          <div className="flex items-center gap-1 mt-0.5 min-w-0 text-xs text-slate-500 dark:text-slate-400">
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="truncate">
              {site.client_name && `${site.client_name} · `}
              {site.city}, {site.state}
              {site.project_id && ` · ${site.project_id}`}
            </span>
          </div>
          {site.source === 'delegated' && site.delegated_from && (
            <p className="text-xs text-purple-500 dark:text-purple-400 mt-0.5">
              from {site.delegated_from}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <DirectionsLink
            latitude={site.latitude}
            longitude={site.longitude}
            compact
          />
          <Link
            to={`/lite/inspect/${site.site_id}${isRainVisit(site.inspection_type) ? '?rain=1' : ''}`}
            className="px-2.5 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            <Play className="w-3 h-3" />
          </Link>
          <button
            onClick={onToggleDayPicker}
            className={`p-1.5 rounded-lg ${
              assignMode
                ? 'text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
            }`}
            title={assignMode ? 'Assign to day' : 'Reschedule'}
          >
            <ArrowRightLeft className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onRemove(site.inspection_id)}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
            title="Remove"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {showDayPicker && (
        <div className="mt-2">
          <DayPicker
            weekDays={weekDays}
            currentDate={currentDate}
            allowEveryWeek
            onSelect={(newDate, everyWeek) =>
              onReschedule(site.inspection_id, newDate, everyWeek)
            }
            onClose={onToggleDayPicker}
          />
        </div>
      )}
    </div>
  );
}
